import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Mail, ArrowDown, Cpu, Network } from 'lucide-react';
import { FaGithub, FaLinkedinIn, FaBrain } from 'react-icons/fa';

const Intro = () => {
  const [typed, setTyped] = useState('');
  const fullText = "AI / ML Engineer";
  const additionalText = "Building Intelligent Systems";
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 300], [0, -50]);
  
  // Particle animation setup
  const [particles, setParticles] = useState([]);
  
  useEffect(() => {
    // Initialize particles
    const particleCount = 50;
    const newParticles = [];
    
    for (let i = 0; i < particleCount; i++) {
      newParticles.push({
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        size: Math.random() * 3 + 1,
        speedX: (Math.random() - 0.5) * 0.5,
        speedY: (Math.random() - 0.5) * 0.5,
        opacity: Math.random() * 0.5 + 0.1
      });
    }
    
    setParticles(newParticles);
    
    // Particle animation loop
    const interval = setInterval(() => {
      setParticles(prevParticles => 
        prevParticles.map(p => ({
          ...p,
          x: (p.x + p.speedX + window.innerWidth) % window.innerWidth,
          y: (p.y + p.speedY + window.innerHeight) % window.innerHeight
        }))
      );
    }, 50);
    
    return () => clearInterval(interval);
  }, []);

  // Typing effect
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (typed.length < fullText.length) {
        setTyped(fullText.slice(0, typed.length + 1));
      }
    }, 50);

    return () => clearTimeout(timeout);
  }, [typed]);

  return (
    <section className="min-h-screen bg-black flex flex-col justify-center items-center relative overflow-hidden">
      {/* Neural network background pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[length:20px_20px] opacity-20" />
      
      {/* Animated particles */}
      {particles.map((particle, index) => (
        <motion.div
          key={index}
          className="absolute rounded-full bg-white"
          style={{
            x: particle.x,
            y: particle.y,
            width: particle.size,
            height: particle.size,
            opacity: particle.opacity,
          }}
          animate={{
            opacity: [particle.opacity, particle.opacity * 1.5, particle.opacity],
          }}
          transition={{
            duration: 2 + Math.random() * 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      ))}

      {/* Main content */}
      <motion.div
        style={{ y }}
        className="text-center px-4 relative z-10"
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.5, type: "spring" }}
          className="relative inline-block mb-8"
        >
          <motion.div 
            className="absolute -inset-8 bg-gradient-to-r from-white/5 to-gray-500/5 rounded-full blur-3xl"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.5, 0.3]
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <h1 className="text-6xl md:text-8xl font-bold relative">
            <span className="bg-gradient-to-b from-white to-gray-500 bg-clip-text text-transparent">
              Arjun Shukla
            </span>
          </h1>
        </motion.div>

        <motion.div className="relative mb-4">
          <motion.p className="text-xl md:text-2xl text-gray-400 font-light h-8">
            {typed}
            <span className="animate-blink">|</span>
          </motion.p>
          <motion.div
            className="absolute -left-4 -right-4 h-px bg-gray-700"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 2, duration: 1 }}
          />
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="text-xl md:text-2xl text-gray-400 font-light mb-8"
        >
          {additionalText}
        </motion.p>

        {/* AI/ML themed decorative elements */}
        <motion.div
          className="absolute left-0 right-0 flex justify-between opacity-10 pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.05 }}
          transition={{ delay: 0.5, duration: 2 }}
        >
          <Cpu className="w-16 h-16 md:w-24 md:h-24 text-gray-700" />
          <Network className="w-16 h-16 md:w-24 md:h-24 text-gray-700" />
        </motion.div>

        <SocialLinks />
      </motion.div>

      <ScrollIndicator />
    </section>
  );
};

const SocialLinks = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.8, duration: 0.8 }}
    className="flex gap-8 justify-center"
  >
    {[
      { href: "https://github.com/arjun988/", icon: <FaGithub className="w-6 h-6" />, label: "GitHub" },
      { href: "https://www.linkedin.com/in/arjun-shukla-1a6579227/", icon: <FaLinkedinIn className="w-6 h-6" />, label: "LinkedIn" },
      { href: "mailto:arjunshukla6558@gmail.com", icon: <Mail className="w-6 h-6" />, label: "Email" },
      { href: "#", icon: <FaBrain className="w-6 h-6" />, label: "AI Projects" }
    ].map((link, index) => (
      <SocialLink key={index} {...link} />
    ))}
  </motion.div>
);

const SocialLink = ({ href, icon, label }) => (
  <motion.a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="group relative p-4 rounded-full bg-white/5 hover:bg-white/10 transition-all duration-300"
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.95 }}
  >
    <motion.div
      className="absolute inset-0 bg-gradient-to-r from-white/10 to-gray-500/10 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
      whileHover={{ scale: 1.2 }}
    />
    <div className="relative text-gray-300 group-hover:text-white transition-colors duration-300">
      {icon}
    </div>
    <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-sm text-gray-400 opacity-0 group-hover:opacity-100 transition-all duration-300 whitespace-nowrap">
      {label}
    </span>
  </motion.a>
);

const ScrollIndicator = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ delay: 1.2, duration: 0.8 }}
    className="absolute bottom-12"
  >
    <motion.div
      animate={{
        y: [0, 10, 0],
        opacity: [0.5, 1, 0.5],
      }}
      transition={{
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut",
      }}
      className="cursor-pointer flex flex-col items-center"
    >
      <motion.span 
        className="text-gray-500 text-sm mb-2"
        animate={{ opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        Explore
      </motion.span>
      <ArrowDown className="w-8 h-8 text-gray-400" />
    </motion.div>
  </motion.div>
);

export default Intro;